/**
 * OpenAI Realtime Engine
 *
 * WebRTC-based voice conversation engine using OpenAI's Realtime API (GA).
 * Handles bidirectional audio streaming, function calling for RAG integration,
 * and usage/cost tracking — all through a single persistent WebRTC session.
 *
 * Audio flow: Browser mic → WebRTC → OpenAI → WebRTC → Browser speakers
 * Events:    Data channel ("oai-events") ← JSON → OpenAI
 *
 * Supports two interaction modes:
 *   Push-to-talk (PTT): Mic muted by default, unmute while talking
 *   Open-mic: Server VAD detects speech, auto-creates responses
 *
 * Key differences from pipeline engine:
 *   - No AudioCaptureService, Deepgram STT, or ElevenLabs TTS
 *   - Single persistent WebRTC session (not per-turn connections)
 *   - RAG integrated via function calling (search_knowledge_base)
 *   - Usage tracking from response.done event (not per-service)
 */

import type {
  VoiceConversationState,
  VoiceConversationMode,
  TurnUsage,
  LLMUsage,
  RealtimeModel,
  RealtimeVoice,
  TurnDetectionMode,
} from '@/types/voice'
import type { VoiceEngineCallbacks } from './VoiceConversationEngine'
import { estimateRealtimeCost } from './pricingRates'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RealtimeEngineConfig {
  /** OpenAI Realtime model */
  model: RealtimeModel
  /** Voice for AI responses */
  voice: RealtimeVoice
  /** Turn detection mode (server_vad or semantic_vad) */
  turnDetection: TurnDetectionMode
  /** Interaction mode */
  mode: VoiceConversationMode
  /** System instructions for the AI */
  instructions?: string
  /** RAG server URL for function calls */
  ragServerUrl: string
  /** RAG API key for function calls */
  ragApiKey: string
}

/** Extended callbacks — adds assistant transcript for chat display */
export interface RealtimeEngineCallbacks extends VoiceEngineCallbacks {
  /** Complete AI response transcript (for adding to chat history) */
  onAssistantTranscript?: (text: string) => void
}

// Audio level polling (~30fps)
const AUDIO_LEVEL_INTERVAL_MS = 33

// Session timeout management
const SESSION_WARN_MS = 55 * 60 * 1000 // 55 min warning
const SESSION_LIMIT_MS = 60 * 60 * 1000 // 60 min hard limit

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class OpenAIRealtimeEngine {
  private callbacks: RealtimeEngineCallbacks
  private config: RealtimeEngineConfig

  // WebRTC resources
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private localStream: MediaStream | null = null
  private localAudioTrack: MediaStreamTrack | null = null
  private audioElement: HTMLAudioElement | null = null

  // Audio level monitoring
  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private audioLevelTimer: ReturnType<typeof setInterval> | null = null

  // Core state
  private _state: VoiceConversationState = 'idle'
  private _destroyed = false
  private _isOpenMicActive = false
  private _isSpeechDetected = false

  // Turn tracking
  private _turnStartedAt = 0
  private _partialAssistantTranscript = ''
  private _userTranscript = ''
  private _pendingFunctionCalls = 0

  // Session timers
  private _sessionWarnTimer: ReturnType<typeof setTimeout> | null = null
  private _sessionLimitTimer: ReturnType<typeof setTimeout> | null = null

  // Open-mic token management
  private _tokenFetcher: (() => Promise<string>) | null = null

  // Session-ready promise (resolved when session.created arrives)
  private _sessionReadyResolve: (() => void) | null = null
  private _sessionReadyReject: ((err: Error) => void) | null = null

  constructor(callbacks: RealtimeEngineCallbacks, config: RealtimeEngineConfig) {
    this.callbacks = callbacks
    this.config = config
  }

  // =========================================================================
  // Public Getters
  // =========================================================================

  get state(): VoiceConversationState {
    return this._state
  }

  get isOpenMicActive(): boolean {
    return this._isOpenMicActive
  }

  get isSpeechDetected(): boolean {
    return this._isSpeechDetected
  }

  /** Whether the WebRTC session is active (data channel open) */
  get isSessionActive(): boolean {
    return this.dc?.readyState === 'open'
  }

  // =========================================================================
  // Session Lifecycle
  // =========================================================================

  /**
   * Connect to OpenAI Realtime via WebRTC.
   * @param ephemeralToken - Token from POST /api/voice/openai-ephemeral-key
   */
  async connect(ephemeralToken: string): Promise<void> {
    if (this._destroyed) return
    if (this._state !== 'idle' && this._state !== 'error') return

    this.setState('connecting')

    try {
      // 1. Acquire microphone
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
        },
      })
      this.localAudioTrack = this.localStream.getAudioTracks()[0]

      // In PTT mode, start with mic muted (user unmutes via startTalking)
      if (this.config.mode === 'push-to-talk') {
        this.localAudioTrack.enabled = false
      }

      // 2. Set up input audio level monitoring
      this.setupAudioLevelMonitoring()

      // 3. Create RTCPeerConnection
      this.pc = new RTCPeerConnection()

      // 4. Add local audio track to connection
      this.pc.addTrack(this.localAudioTrack, this.localStream)

      // 5. Create data channel for bidirectional JSON events
      this.dc = this.pc.createDataChannel('oai-events')
      this.setupDataChannel()

      // 6. Handle remote audio track (AI voice output)
      this.pc.ontrack = (event) => this.handleRemoteTrack(event)

      // 7. Create and set local SDP offer
      const offer = await this.pc.createOffer()
      await this.pc.setLocalDescription(offer)

      // 8. Exchange SDP with OpenAI's Realtime endpoint
      const sdpResponse = await fetch(
        `https://api.openai.com/v1/realtime?model=${this.config.model}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${ephemeralToken}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        }
      )

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text().catch(() => '')
        throw new Error(
          `WebRTC SDP exchange failed: ${sdpResponse.status} ${errorText.slice(0, 200)}`
        )
      }

      const answerSdp = await sdpResponse.text()

      // 9. Set remote SDP answer
      await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })

      // 10. Wait for session.created via data channel (confirms session is ready)
      await this.waitForSessionReady()

      // 11. Start session timeout timers
      this.startSessionTimers()
    } catch (error) {
      this.callbacks.onError(
        error instanceof Error ? error : new Error(String(error))
      )
      this.setState('error')
      this.cleanup()
    }
  }

  /** Gracefully close the WebRTC session */
  disconnect(): void {
    this.cleanup()
    this._isOpenMicActive = false
    this._isSpeechDetected = false
    this.setState('idle')
  }

  /** Full cleanup — releases all resources, marks engine as destroyed */
  destroy(): void {
    this._destroyed = true
    this.cleanup()
  }

  // =========================================================================
  // Push-to-Talk Controls
  // =========================================================================

  /** Start talking (PTT): unmute mic and begin capturing audio */
  startTalking(): void {
    if (this._destroyed || !this.localAudioTrack) return
    if (this._state !== 'listening' && this._state !== 'idle') return

    this.resetTurnState()
    this.localAudioTrack.enabled = true
    this.setState('listening')
  }

  /** Stop talking (PTT): mute mic, commit audio buffer, request AI response */
  stopTalking(): void {
    if (!this.localAudioTrack || !this.dc) return
    if (this._state !== 'listening') return

    this.localAudioTrack.enabled = false
    this.sendEvent({ type: 'input_audio_buffer.commit' })
    this.sendEvent({ type: 'response.create' })
    // State transitions to 'processing' when response.created arrives
  }

  // =========================================================================
  // Open-Mic Controls
  // =========================================================================

  /**
   * Activate open-mic mode.
   * Fetches an ephemeral key, establishes WebRTC, and configures server VAD.
   */
  async startOpenMic(tokenFetcher: () => Promise<string>): Promise<void> {
    if (this._destroyed || this._isOpenMicActive) return

    this._tokenFetcher = tokenFetcher
    this._isOpenMicActive = true

    try {
      const token = await tokenFetcher()
      await this.connect(token)
      // Mic stays enabled (connect() checks mode and skips muting for open-mic)
    } catch (error) {
      this._isOpenMicActive = false
      this._tokenFetcher = null
      this.callbacks.onError(
        error instanceof Error ? error : new Error(String(error))
      )
      this.setState('error')
    }
  }

  /** Deactivate open-mic mode and close the session */
  async stopOpenMic(): Promise<void> {
    this._isOpenMicActive = false
    this._isSpeechDetected = false
    this._tokenFetcher = null
    this.callbacks.onSpeechDetected(false)
    this.disconnect()
  }

  // =========================================================================
  // Interrupt / Cancel
  // =========================================================================

  /** Interrupt the current AI response (barge-in) */
  interrupt(): void {
    if (this._state === 'speaking' || this._state === 'processing') {
      this.sendEvent({ type: 'response.cancel' })
      this._partialAssistantTranscript = ''
      this.callbacks.onPartialTranscript('')
      this.setState(this._isOpenMicActive ? 'listening' : 'idle')
    }
  }

  /** Cancel current operation and return to idle/listening */
  cancel(): void {
    this.interrupt()
  }

  // =========================================================================
  // TTS Bridge (no-ops — WebRTC handles audio internally)
  // =========================================================================

  notifyTTSStarted(): void {
    /* no-op: audio output handled by WebRTC */
  }
  notifyTTSFinished(): void {
    /* no-op: state transitions driven by data channel events */
  }
  trackTTSCharacters(_n: number): void {
    /* no-op: no separate TTS service */
  }
  reportLLMUsage(_u: unknown): void {
    /* no-op: usage extracted from response.done */
  }
  emitTurnUsage(): void {
    /* no-op: usage emitted from response.done handler */
  }

  // =========================================================================
  // Configuration
  // =========================================================================

  /** Update configuration (e.g., when settings change between sessions) */
  updateConfig(config: Partial<RealtimeEngineConfig>): void {
    this.config = { ...this.config, ...config }
  }

  // =========================================================================
  // Private — Data Channel Setup
  // =========================================================================

  private setupDataChannel(): void {
    if (!this.dc) return

    this.dc.onopen = () => {
      console.debug('[RealtimeEngine] Data channel opened')
      this.sendSessionUpdate()
    }

    this.dc.onmessage = (event: MessageEvent) => {
      try {
        const serverEvent = JSON.parse(event.data)
        this.handleServerEvent(serverEvent)
      } catch (error) {
        console.warn('[RealtimeEngine] Failed to parse server event:', error)
      }
    }

    this.dc.onclose = () => {
      console.debug('[RealtimeEngine] Data channel closed')
      this._sessionReadyReject?.(
        new Error('Data channel closed before session created')
      )
      if (!this._destroyed && this._state !== 'idle') {
        this.setState('idle')
      }
    }

    this.dc.onerror = (event) => {
      console.error('[RealtimeEngine] Data channel error:', event)
      this._sessionReadyReject?.(
        new Error('Data channel error before session created')
      )
      this.callbacks.onError(new Error('WebRTC data channel error'))
      this.setState('error')
    }
  }

  private sendEvent(event: Record<string, unknown>): void {
    if (this.dc?.readyState === 'open') {
      this.dc.send(JSON.stringify(event))
    }
  }

  private sendSessionUpdate(): void {
    const session: Record<string, unknown> = {
      instructions:
        this.config.instructions ||
        'You are a helpful AI assistant. Use the search_knowledge_base function to find relevant information when answering questions.',
      input_audio_transcription: {
        model: 'whisper-1',
      },
      voice: this.config.voice,
    }

    // Configure turn detection based on mode
    if (this.config.mode === 'open-mic') {
      session.turn_detection = { type: this.config.turnDetection }
    } else {
      // PTT: disable server-side turn detection (client controls turns)
      session.turn_detection = null
    }

    this.sendEvent({ type: 'session.update', session })
  }

  // =========================================================================
  // Private — Server Event Handling
  // =========================================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleServerEvent(event: Record<string, any>): void {
    switch (event.type) {
      // --- Session lifecycle ---
      case 'session.created':
        console.debug('[RealtimeEngine] Session created:', event.session?.id)
        this.setState('listening')
        this._sessionReadyResolve?.()
        break

      case 'session.updated':
        console.debug('[RealtimeEngine] Session updated')
        break

      // --- Input audio / speech detection (open-mic) ---
      case 'input_audio_buffer.speech_started':
        this._isSpeechDetected = true
        this.callbacks.onSpeechDetected(true)
        this.resetTurnState()
        break

      case 'input_audio_buffer.speech_stopped':
        this._isSpeechDetected = false
        this.callbacks.onSpeechDetected(false)
        break

      case 'input_audio_buffer.committed':
        // Audio buffer committed — logged for debugging
        break

      // --- User transcript (Whisper transcription of user's speech) ---
      case 'conversation.item.input_audio_transcription.completed':
        this._userTranscript = event.transcript || ''
        if (this._userTranscript) {
          this.callbacks.onFinalTranscript(this._userTranscript)
        }
        break

      case 'conversation.item.input_audio_transcription.failed':
        console.warn(
          '[RealtimeEngine] Input audio transcription failed:',
          event.error
        )
        break

      // --- Response lifecycle ---
      case 'response.created':
        this.setState('processing')
        if (this._turnStartedAt === 0) {
          this._turnStartedAt = Date.now()
        }
        break

      // --- Assistant audio transcript (GA event names) ---
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        this._partialAssistantTranscript += event.delta || ''
        this.callbacks.onPartialTranscript(this._partialAssistantTranscript)
        // First transcript delta means audio generation has begun
        if (this._state === 'processing') {
          this.setState('speaking')
        }
        break

      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
        if (
          this.callbacks.onAssistantTranscript &&
          this._partialAssistantTranscript
        ) {
          this.callbacks.onAssistantTranscript(
            this._partialAssistantTranscript
          )
        }
        break

      // --- Function calling ---
      case 'response.function_call_arguments.done':
        this._pendingFunctionCalls++
        this.handleFunctionCall(event.call_id, event.name, event.arguments)
        break

      // --- Response complete ---
      case 'response.done':
        this.handleResponseDone(event)
        break

      // --- Errors ---
      case 'error':
        console.error('[RealtimeEngine] Server error:', event.error)
        this.callbacks.onError(
          new Error(event.error?.message || 'OpenAI Realtime error')
        )
        // Recoverable errors (e.g., invalid request) don't kill the session
        if (
          event.error?.type !== 'invalid_request_error' &&
          event.error?.code !== 'buffer_too_small'
        ) {
          this.setState('error')
        }
        break

      default:
        // Silently ignore unhandled event types
        break
    }
  }

  // =========================================================================
  // Private — Function Call Handling
  // =========================================================================

  private async handleFunctionCall(
    callId: string,
    name: string,
    argsStr: string
  ): Promise<void> {
    if (name !== 'search_knowledge_base') {
      this.sendFunctionOutput(callId, `Unknown function: ${name}`)
      this._pendingFunctionCalls--
      return
    }

    try {
      const args = JSON.parse(argsStr)
      const query: string = args.query || ''

      // Resolve RAG server URL (handle relative paths like /rag-proxy)
      const ragUrl = this.config.ragServerUrl.startsWith('/')
        ? `${window.location.origin}${this.config.ragServerUrl}`
        : this.config.ragServerUrl

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (this.config.ragApiKey) {
        headers['x-api-key'] = this.config.ragApiKey
      }

      const response = await fetch(`${ragUrl}/v1/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content:
                'Answer the query using the knowledge base. Be concise and factual.',
            },
            { role: 'user', content: query },
          ],
          stream: false,
        }),
      })

      let resultText = 'No results found.'
      if (response.ok) {
        const data = await response.json()
        resultText =
          data.choices?.[0]?.message?.content ?? 'No results found.'
      } else {
        console.warn('[RealtimeEngine] RAG server error:', response.status)
        resultText = 'Knowledge base search failed. Please try again.'
      }

      this.sendFunctionOutput(callId, resultText)
    } catch (error) {
      console.error('[RealtimeEngine] Function call error:', error)
      this.sendFunctionOutput(callId, 'Error searching knowledge base.')
    } finally {
      this._pendingFunctionCalls--
    }
  }

  /** Send function call output back to Realtime API and trigger next response */
  private sendFunctionOutput(callId: string, output: string): void {
    this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output,
      },
    })
    this.sendEvent({ type: 'response.create' })
  }

  // =========================================================================
  // Private — Response Completion & Usage
  // =========================================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleResponseDone(event: Record<string, any>): void {
    // Wait for all pending function calls to complete before transitioning
    if (this._pendingFunctionCalls > 0) {
      return
    }

    // Emit usage data (if available)
    this.emitTurnUsageFromResponse(event)

    // Reset assistant transcript for next turn
    this._partialAssistantTranscript = ''
    this.callbacks.onPartialTranscript('')

    // Transition to next state
    if (this._isOpenMicActive) {
      this.setState('listening')
    } else {
      this.setState('idle')
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private emitTurnUsageFromResponse(event: Record<string, any>): void {
    const usage = event.response?.usage
    if (!usage) return

    const now = Date.now()
    const model = this.config.model

    // Build LLM usage from OpenAI's token breakdown
    const inputDetails = usage.input_token_details ?? {}
    const outputDetails = usage.output_token_details ?? {}
    const cachedDetails = inputDetails.cached_tokens_details ?? {}

    const llmUsage: LLMUsage = {
      provider: 'openai',
      model,
      promptTokens: usage.input_tokens ?? 0,
      completionTokens: usage.output_tokens ?? 0,
      totalTokens:
        usage.total_tokens ??
        (usage.input_tokens ?? 0) + (usage.output_tokens ?? 0),
      cachedTokens: inputDetails.cached_tokens,
      audioInputTokens: inputDetails.audio_tokens,
      audioOutputTokens: outputDetails.audio_tokens,
      estimatedCost: 0,
    }

    // Calculate cost using existing pricing utility
    llmUsage.estimatedCost = estimateRealtimeCost(model, {
      textInputTokens: inputDetails.text_tokens ?? 0,
      textOutputTokens: outputDetails.text_tokens ?? 0,
      audioInputTokens: inputDetails.audio_tokens ?? 0,
      audioOutputTokens: outputDetails.audio_tokens ?? 0,
      cachedTextTokens: cachedDetails.text_tokens,
      cachedAudioTokens: cachedDetails.audio_tokens,
    })

    const turnUsage: TurnUsage = {
      turnStartedAt: this._turnStartedAt,
      turnCompletedAt: now,
      llm: llmUsage,
      // STT and TTS are internal to Realtime API (not tracked separately)
      stt: undefined,
      tts: undefined,
      totalEstimatedCost: llmUsage.estimatedCost,
      latencyMs:
        this._turnStartedAt > 0 ? now - this._turnStartedAt : undefined,
    }

    this.callbacks.onTurnUsage(turnUsage)
  }

  // =========================================================================
  // Private — Remote Audio Track
  // =========================================================================

  private handleRemoteTrack(event: RTCTrackEvent): void {
    this.audioElement = document.createElement('audio')
    this.audioElement.autoplay = true
    this.audioElement.srcObject = event.streams[0]
    console.debug('[RealtimeEngine] Remote audio track received')
  }

  // =========================================================================
  // Private — Audio Level Monitoring (Input Mic)
  // =========================================================================

  private setupAudioLevelMonitoring(): void {
    if (!this.localStream) return

    try {
      this.audioContext = new AudioContext()
      const source = this.audioContext.createMediaStreamSource(this.localStream)
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 256
      source.connect(this.analyser)

      const dataArray = new Uint8Array(this.analyser.frequencyBinCount)

      this.audioLevelTimer = setInterval(() => {
        if (!this.analyser || !this.localAudioTrack?.enabled) {
          this.callbacks.onAudioLevel(0)
          return
        }

        this.analyser.getByteFrequencyData(dataArray)
        let sum = 0
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i]
        }
        const level = sum / dataArray.length / 255
        this.callbacks.onAudioLevel(level)
      }, AUDIO_LEVEL_INTERVAL_MS)
    } catch {
      // AudioContext unavailable — skip level monitoring
    }
  }

  // =========================================================================
  // Private — State Management
  // =========================================================================

  private setState(state: VoiceConversationState): void {
    if (this._state === state) return
    this._state = state
    this.callbacks.onStateChange(state)
  }

  private resetTurnState(): void {
    this._turnStartedAt = Date.now()
    this._partialAssistantTranscript = ''
    this._userTranscript = ''
  }

  /** Returns a promise that resolves when session.created arrives via data channel */
  private waitForSessionReady(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this._sessionReadyResolve = null
        this._sessionReadyReject = null
        reject(new Error('Session creation timed out (10s)'))
      }, 10_000)

      this._sessionReadyResolve = () => {
        clearTimeout(timeout)
        this._sessionReadyResolve = null
        this._sessionReadyReject = null
        resolve()
      }
      this._sessionReadyReject = (err: Error) => {
        clearTimeout(timeout)
        this._sessionReadyResolve = null
        this._sessionReadyReject = null
        reject(err)
      }
    })
  }

  // =========================================================================
  // Private — Session Timers
  // =========================================================================

  private startSessionTimers(): void {
    this.clearSessionTimers()

    this._sessionWarnTimer = setTimeout(() => {
      console.warn('[RealtimeEngine] Session approaching 60-minute limit')
    }, SESSION_WARN_MS)

    this._sessionLimitTimer = setTimeout(() => {
      console.warn(
        '[RealtimeEngine] Session reached 60-minute limit, disconnecting'
      )
      const wasOpenMic = this._isOpenMicActive
      const fetcher = this._tokenFetcher

      this.disconnect()

      // Auto-reconnect in open-mic mode
      if (wasOpenMic && fetcher) {
        console.debug('[RealtimeEngine] Auto-reconnecting open-mic session')
        this.startOpenMic(fetcher)
      }
    }, SESSION_LIMIT_MS)
  }

  private clearSessionTimers(): void {
    if (this._sessionWarnTimer) {
      clearTimeout(this._sessionWarnTimer)
      this._sessionWarnTimer = null
    }
    if (this._sessionLimitTimer) {
      clearTimeout(this._sessionLimitTimer)
      this._sessionLimitTimer = null
    }
  }

  // =========================================================================
  // Private — Cleanup
  // =========================================================================

  private cleanup(): void {
    this.clearSessionTimers()

    // Stop audio level monitoring
    if (this.audioLevelTimer) {
      clearInterval(this.audioLevelTimer)
      this.audioLevelTimer = null
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
      this.analyser = null
    }

    // Close data channel
    if (this.dc) {
      this.dc.close()
      this.dc = null
    }

    // Close peer connection
    if (this.pc) {
      this.pc.close()
      this.pc = null
    }

    // Stop local media tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop())
      this.localStream = null
      this.localAudioTrack = null
    }

    // Remove audio element
    if (this.audioElement) {
      this.audioElement.srcObject = null
      this.audioElement = null
    }
  }
}
